// Out-of-buffer Go to Definition targets, as locations Monaco can resolve.
//
// Monaco calls `provideDefinition` for the Ctrl/Cmd+hover link probe as well
// as for real navigations (F12, Ctrl/Cmd+click, context-menu Go to
// Definition), and nothing in the call says which. So the provider must stay
// side-effect free: for a table/view, column or routine it returns a location
// under the `jamsql-nav:` scheme (`definitionNavTarget.ts`), and the
// navigation (Object Explorer reveal, Table Designer, CREATE-script tab,
// overload picker) runs in the editor opener (`definitionNavigationOpener.ts`),
// which Monaco only calls when it actually opens the location.
//
// Every returned URI needs a real model: Monaco standalone resolves a location
// through the model registry, both for the hover preview
// (`goToDefinitionAtPosition.js`, an un-caught `createModelReference(...).then`)
// and for the Peek Definition widget (`FileReferences.resolve`, which routes a
// miss to `onUnexpectedError`). So each target gets a one-line model that
// describes the navigation. The hover card and the peek row show that line;
// opening the peek row navigates like F12.
//
// URIs are deterministic per target, so re-probing the same name reuses its
// model, and the registry is capped (LRU) so a long session of hovering cannot
// grow Monaco's model registry without bound. Eviction skips any model an
// editor is showing — in practice the preview of an open Peek Definition — so
// a peek left open while the user hovers elsewhere keeps its row working. The
// Monaco namespace is injected so unit tests can drive the registry.

import type * as monacoNs from 'monaco-editor';

import type { DefinitionNavTarget } from './definitionNavTarget';
import { buildDefinitionNavUri, describeDefinitionNavTarget } from './definitionNavTarget';

/** Most recent targets kept alive (plus any model an editor still shows). A
 *  hover card resolves its model once, a peek preview holds it while open, so
 *  a small cap is plenty. */
export const MAX_DEFINITION_NAV_TARGETS = 32;

/** Structural subset of the Monaco namespace the registry needs. */
export type DefinitionNavMonacoApi = {
  readonly Uri: { parse: (value: string) => monacoNs.Uri };
  readonly editor: {
    createModel: (
      value: string,
      language?: string,
      uri?: monacoNs.Uri,
    ) => monacoNs.editor.ITextModel;
    getModel: (uri: monacoNs.Uri) => monacoNs.editor.ITextModel | null;
  };
};

type Entry = { readonly nav: DefinitionNavTarget; readonly model: monacoNs.editor.ITextModel };

/** Insertion order = recency (re-registering moves an entry to the end). */
const entries = new Map<string, Entry>();

/** Register `nav` and return the location the DefinitionProvider hands Monaco.
 *  `range` spans the one-line description (the hover card previews exactly
 *  that range); `targetSelectionRange` is a 0-width point at its start, which
 *  is what the Peek Definition row reveals. */
export function definitionNavLocation(
  monaco: DefinitionNavMonacoApi,
  nav: DefinitionNavTarget,
): monacoNs.languages.LocationLink {
  const model = ensureNavModel(monaco, nav);
  return {
    uri: model.uri,
    range: model.getFullModelRange(),
    targetSelectionRange: { startLineNumber: 1, startColumn: 1, endLineNumber: 1, endColumn: 1 },
  };
}

/** The target behind a `jamsql-nav:` URI (`Uri.toString()` form), or `null`
 *  for URIs we never minted or already evicted. */
export function lookupDefinitionNavTarget(uri: string): DefinitionNavTarget | null {
  const entry = entries.get(uri);
  if (!entry) return null;
  if (entry.model.isDisposed()) {
    entries.delete(uri);
    return null;
  }
  return entry.nav;
}

/** Test-only — dispose every registered model and forget the registry. */
export function __resetDefinitionNavTargetsForTest(): void {
  for (const entry of entries.values()) {
    if (!entry.model.isDisposed()) entry.model.dispose();
  }
  entries.clear();
}

function ensureNavModel(
  monaco: DefinitionNavMonacoApi,
  nav: DefinitionNavTarget,
): monacoNs.editor.ITextModel {
  const uri = monaco.Uri.parse(buildDefinitionNavUri(nav));
  // Key on the round-tripped form: it is what an editor opener receives.
  const key = uri.toString();
  const text = describeDefinitionNavTarget(nav);

  const cached = entries.get(key);
  let model = cached && !cached.model.isDisposed() ? cached.model : null;
  if (!model) {
    // A model can outlive its registry entry across a renderer hot reload;
    // adopt it instead of colliding on `createModel` (throws on duplicate URI).
    const existing = monaco.editor.getModel(uri);
    model = existing && !existing.isDisposed() ? existing : monaco.editor.createModel(text, 'plaintext', uri);
  }
  if (model.getValue() !== text) model.setValue(text);

  entries.delete(key);
  entries.set(key, { nav, model });
  evictOverflow(key);
  return model;
}

/** Drop the least recently used entries beyond the cap, oldest first, but
 *  never `keep` (the entry being returned right now) and never one whose model
 *  an editor is showing (an open Peek Definition's preview): disposing it
 *  would blank that peek and orphan its row. When every older entry is pinned
 *  that way the registry runs over the cap until those peeks close; the
 *  pinned entries are revisited on the next registration. */
function evictOverflow(keep: string): void {
  let excess = entries.size - MAX_DEFINITION_NAV_TARGETS;
  if (excess <= 0) return;
  for (const [key, entry] of entries) {
    if (excess <= 0) return;
    if (key === keep) continue;
    if (entry.model.isDisposed()) {
      entries.delete(key);
      excess -= 1;
      continue;
    }
    if (entry.model.isAttachedToEditor()) continue;
    entries.delete(key);
    entry.model.dispose();
    excess -= 1;
  }
}
